/**
 * Application Utility Functions
 * Centralized helpers for application status, formatting, and display logic
 */

// Application status constants
export const APPLICATION_STATUS = {
  PENDING: 'pending',
  SHORTLISTED: 'shortlisted',
  INTERVIEWED: 'interviewed',
  HIRED: 'hired',
  REJECTED: 'rejected',
  ARCHIVED: 'archived'
};

/**
 * Get status color classes for application status badges
 * @param {string} status - Application status
 * @returns {string} Tailwind CSS classes
 */
export const getStatusColor = (status) => {
  const colorMap = {
    pending: 'bg-warning-100 text-warning border-warning-200',
    shortlisted: 'bg-primary-100 text-primary border-primary-200',
    interviewed: 'bg-accent-100 text-accent border-accent-200',
    hired: 'bg-success-100 text-success border-success-200',
    rejected: 'bg-error-100 text-error border-error-200',
    archived: 'bg-secondary-100 text-text-muted border-secondary-200'
  };
  return colorMap[status] || 'bg-secondary-100 text-text-secondary border-secondary-200';
};

/**
 * Get icon name for application status
 * @param {string} status - Application status
 * @returns {string} Icon name
 */
export const getStatusIcon = (status) => {
  const iconMap = {
    pending: 'Clock',
    shortlisted: 'Star',
    interviewed: 'Calendar',
    hired: 'Award',
    rejected: 'XCircle',
    archived: 'Archive'
  };
  return iconMap[status] || 'FileText';
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date with time for detailed views
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date with time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'Not specified';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format currency for hourly rate display
 * @param {number} amount - Hourly rate amount
 * @returns {string} Formatted currency
 */
export const formatCurrency = (amount) => {
  if (!amount) return 'Not specified';
  return `$${amount}/hr`;
};

/**
 * Extract employer ID from application object with fallback paths
 * @param {Object} application - Application object
 * @returns {string|null} Employer ID or null
 */
export const getEmployerId = (application) => {
  return application?.job?.employer_id || 
         application?.job?.employer?.id ||
         application?.employer_id ||
         null;
};

/**
 * Get company name from application with fallback paths
 * @param {Object} application - Application object
 * @returns {string} Company name
 */
export const getCompanyName = (application) => {
  return application?.job?.company_name || 
         application?.job?.employer?.company_name || 
         application?.job?.employer?.full_name || 
         'Unknown Company';
};

/**
 * Filter applications based on search query and status
 * @param {Array} applications - Array of applications
 * @param {string} searchQuery - Search query string
 * @param {string} statusFilter - Status filter value
 * @returns {Array} Filtered applications
 */
export const filterApplications = (applications, searchQuery, statusFilter) => {
  if (!applications?.length) return [];
  
  return applications.filter(app => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      app?.job?.title?.toLowerCase().includes(searchLower) ||
      getCompanyName(app).toLowerCase().includes(searchLower);
    
    // When filtering by 'archived', show only archived apps
    if (statusFilter === 'archived') {
      return matchesSearch && app?.is_archived === true;
    }
    
    // When filtering by 'all', exclude archived apps
    if (statusFilter === 'all') {
      return matchesSearch && !app?.is_archived;
    }
    
    // For specific status filters, show matching non-archived apps
    const matchesStatus = app?.status === statusFilter;
    return matchesSearch && matchesStatus && !app?.is_archived;
  });
};

/**
 * Calculate application statistics by status
 * @param {Array} applications - Array of applications
 * @returns {Array} Statistics array with status and count
 */
export const calculateApplicationStats = (applications) => {
  if (!applications?.length) return [];
  
  const stats = Object.values(APPLICATION_STATUS)
    .filter(status => status !== 'archived')
    .map(status => ({
      status,
      count: applications.filter(app => app.status === status && !app.is_archived).length
    }));  
  // Add archived count separately (based on is_archived flag, not status)
  const archivedCount = applications.filter(app => app.is_archived).length;
  stats.push({ status: 'archived', count: archivedCount });
  
  return stats;
};

/**
 * Paginate applications array
 * @param {Array} applications - Array of applications
 * @param {number} page - Current page number
 * @param {number} itemsPerPage - Items per page
 * @returns {Object} Paginated data with items and metadata
 */
export const paginateApplications = (applications, page, itemsPerPage) => {
  const totalPages = Math.ceil(applications.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedItems = applications.slice(startIndex, startIndex + itemsPerPage);
  
  return {
    items: paginatedItems,
    totalPages,
    currentPage: page,
    totalItems: applications.length,
    startIndex,
    endIndex: startIndex + paginatedItems.length
  };
};
